import { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useApi } from "../../../../../hooks/useApi";
import DefaultModalLayout from "../../../../Modal/DefaultModalLayout";
import CalendarLineBtn from "../../../../Button/CalendarLineBtn";
import CalendarModal from "../../../../Modal/CalendarModal";
import dayjs from "dayjs";
import { ModalLabel } from "../../../Users/UserDetail/CommentModal/TargetContainer";

interface AutoExtendModalProps {
  partyId: string;
  uid: number;
  nickname: string;
  name: string;
  grade: number;
  currentAutoExtending: number;
  extendRevDate?: string;
  partyEnddate?: string;
  closeEvent: () => void;
  confirmEvent: () => void;
}

export default function AutoExtendModal({
  partyId,
  uid,
  nickname,
  name,
  grade,
  currentAutoExtending,
  extendRevDate,
  partyEnddate,
  closeEvent,
  confirmEvent,
}: AutoExtendModalProps) {
  const isOwner = grade === 0;

  const [isOn, setIsOn] = useState(currentAutoExtending === 1);
  const [submitClick, setSubmitClick] = useState(false);
  const [selectedDate, setSelectedDate] = useState(extendRevDate ?? "선택");
  const [calendarOpen, setCalendarOpen] = useState(false);

  const needDatePicker = isOwner;
  const minSelectableDate = partyEnddate
    ? dayjs(partyEnddate).add(2, "month").format("YYYY-MM-DD")
    : undefined;

  const isInitialOn = currentAutoExtending === 1;
  const toggleChanged = isOn !== isInitialOn;
  const dateChanged = isOwner && selectedDate !== "선택" && selectedDate !== (extendRevDate ?? "선택");
  const hasChanges = toggleChanged || dateChanged;

  const { response, error } = useApi({
    method: "post",
    url: submitClick
      ? `party/${partyId}/auto-extend`
      : undefined,
    data: {
      uid,
      state: isOn ? 1 : 0,
      ...(isOwner && selectedDate !== "선택"
        ? { enddate: selectedDate }
        : {}),
    },
  });

  useEffect(() => {
    if (response?.status === 200) {
      toast.success(
        isOn
          ? "자동연장이 On으로 변경되었습니다."
          : "자동연장이 Off로 변경되었습니다.",
      );
      closeEvent();
      confirmEvent();
    }
  }, [response]);

  useEffect(() => {
    if (error) {
      setSubmitClick(false);
      toast.error(error?.data ?? error?.message ?? "오류가 발생했습니다.");
    }
  }, [error]);

  const handleConfirm = () => {
    if (!hasChanges) {
      toast.warn("변경사항이 없습니다.");
      return;
    }
    if (isOwner && selectedDate === "선택") {
      toast.warn("종료일을 선택해주세요.");
      return;
    }
    setSubmitClick(true);
  };

  return (
    <DefaultModalLayout
      modalTitle="자동연장 설정"
      
      isMediumModal={true}
      isOverflowVisible={true}
      closeEvent={closeEvent}
      confirmEvent={handleConfirm}
      confirmDisabled={!hasChanges || (isOwner && selectedDate === "선택")}
      confirmBtnText="확인"
    >
      <>
        <Row>
          <ModalLabel>대상</ModalLabel>
          <ValueText>
            {uid}_{name}_{nickname}
          </ValueText>
        </Row>

        <Row>
          <ModalLabel>자동연장</ModalLabel>
          <ToggleWrapper onClick={() => setIsOn((prev) => !prev)}>
            <ToggleTrack isOn={isOn}>
              <ToggleThumb isOn={isOn} />
            </ToggleTrack>
            <ToggleLabel isOn={isOn}>{isOn ? "On" : "Off"}</ToggleLabel>
          </ToggleWrapper>
        </Row>

        {needDatePicker && (
          <Row>
            <ModalLabel>종료일</ModalLabel>
            <CalendarContainer>
              <CalendarLineBtn
                onClickEvent={() => setCalendarOpen(true)}
                value={selectedDate}
                initValue={extendRevDate ?? "선택"}
                disabled={!isOn}
              />

              {calendarOpen && (
                <CalendarModal
                  closeEvent={() => setCalendarOpen(false)}
                  changeEvent={(date: string) => setSelectedDate(date)}
                  dateValue={extendRevDate}
                  minDate={minSelectableDate}
                />
              )}
            </CalendarContainer>
          </Row>
        )}
      </>
    </DefaultModalLayout>
  );
}

const Row = styled.div`
  width: 100%;
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const ValueText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1e1e1e;
`;

const StatusText = styled.span<{ isOn: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ isOn, theme }) =>
    isOn ? theme.buttonColor.blueNormal : theme.color.gray600};
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const ToggleTrack = styled.div<{ isOn: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${({ isOn, theme }) =>
    isOn ? theme.buttonColor.blueNormal : theme.color.gray300};
  position: relative;
  transition: background-color 0.2s ease;
`;

const ToggleThumb = styled.div<{ isOn: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  position: absolute;
  top: 2px;
  left: ${({ isOn }) => (isOn ? "22px" : "2px")};
  transition: left 0.2s ease;
`;

const ToggleLabel = styled.span<{ isOn: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ isOn, theme }) =>
    isOn ? theme.buttonColor.blueNormal : theme.color.gray600};
`;

const CalendarContainer = styled.div`
  position: relative;
`;
